// The admin table look, in one place.
//
// These class strings were the Agencies workspace's private styling. They live
// here now so a second table reads as the same product rather than a lookalike:
// the dark-green header, the pill controls, the filter card and the skeletons
// are all defined once and imported by both pages.
//
// Layout stays with each page — Agencies has configurable, resizable, freezable
// columns and Website Signups has a fixed set. What is shared is the styling,
// not the column machinery.

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { TableCell, TableRow } from '@/components/ui/table';

/** Focus ring used by every control inside an admin table. */
export const controlFocusClass =
  'focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

/** Text inputs and selects on the filter card. */
export const adminInputClass = cn('border-[rgba(6,43,26,0.16)]', controlFocusClass);

/** The card that holds a table: hairline border, soft shadow, clipped corners. */
export const tableCardClass = 'overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm';

/** Wrapper that lets a wide table scroll on its own instead of the page. */
export const tableScrollClass = 'min-w-0 overflow-x-auto';

/** The dark-green header row. */
export const tableHeaderRowClass = 'bg-[#062B1A] hover:bg-[#062B1A]';

/** One header cell: small, uppercase, tracked-out, white on green. */
export const tableHeadCellClass =
  'h-auto min-h-10 whitespace-normal px-1.5 text-[10px] font-semibold uppercase leading-tight tracking-[0.035em] text-white 2xl:text-[11px]';

/**
 * A body row — white, with the same hover the Agencies table uses and an
 * explicit hairline between rows. The shadcn default border colour is a theme
 * variable this app never sets, so the separator has to be named here.
 */
export const tableRowClass =
  'group items-center border-b border-[rgba(6,43,26,0.07)] bg-white hover:bg-[#F7F8FA]/80';

/** A body cell. Row density is passed in by the page. */
export const tableCellClass = 'overflow-hidden px-2 align-middle text-xs 2xl:text-sm';

export const ROW_DENSITY_CLASSES = {
  compact: 'py-1.5',
  comfortable: 'py-2.5',
  spacious: 'py-4',
};

/** Round filter pills above a table. */
export const filterPillClass =
  'h-8 rounded-full border-[#062B1A]/15 px-3 text-xs text-[#062B1A] hover:bg-[#062B1A]/5';
export const filterPillActiveClass = 'border-[#D4AF37] bg-[#062B1A] text-white hover:bg-[#041B10]';

/** Base classes for a status pill; the tone classes come from statusToneClass. */
export const statusPillClass = 'rounded-full border px-2 py-0.5 text-[11px] font-semibold';

/**
 * Status tones, matching the Agencies palette so the same idea reads the same
 * colour across the admin app.
 */
export const STATUS_TONE_CLASSES = {
  gold: 'border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#7A5A05]',
  neutral: 'border-[#062B1A]/15 bg-[#062B1A]/5 text-[#062B1A]/70',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  red: 'border-red-200 bg-red-50 text-red-700',
  orange: 'border-orange-200 bg-orange-50 text-orange-700',
  slate: 'border-slate-200 bg-slate-50 text-slate-600',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

export function statusToneClass(tone) {
  return STATUS_TONE_CLASSES[tone] || 'border-[#062B1A]/12 bg-[#062B1A]/5 text-[#062B1A]/70';
}

/** A read-only status pill. */
export function StatusPill({ tone, children, className }) {
  return (
    <span className={cn(statusPillClass, statusToneClass(tone), className)}>{children}</span>
  );
}

/** The search-and-filters card that sits above a table. */
export function TableFilterCard({ children, className }) {
  return (
    <Card className={cn('border border-[rgba(6,43,26,0.08)] shadow-sm', className)}>
      <CardContent className="flex flex-col gap-4 p-4">{children}</CardContent>
    </Card>
  );
}

/** Loading placeholder shaped like the table it replaces. */
export function TableSkeleton({ rows = 6 }) {
  return (
    <Card className={tableCardClass}>
      <CardContent className="space-y-3 p-5">
        <div className="h-11 animate-pulse rounded bg-[#062B1A]/10" />
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="h-14 animate-pulse rounded bg-[#F7F8FA]" />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * A full-width "nothing here" row.
 *
 * The rows above it are CSS grids rather than table rows, so the table has no
 * column model for a colSpan to span. This row is its own single-column grid,
 * which fills the width whatever the columns above are doing.
 */
export function TableEmptyRow({ colSpan, children }) {
  return (
    <TableRow className="grid bg-white hover:bg-white" style={{ gridTemplateColumns: '1fr' }}>
      <TableCell colSpan={colSpan} className="py-12 text-center text-sm text-[#062B1A]/45">
        {children}
      </TableCell>
    </TableRow>
  );
}

/* ─────────────────────────── Small screens ───────────────────────────
 *
 * These tables are 1,000–1,400px wide at their narrowest, against about
 * 340px of usable width on a phone. Scrolling one sideways to read a row is
 * not reading a table, so below `md` the same rows are rendered again as
 * cards and the table is hidden.
 *
 * Both branches stay in the DOM and CSS decides which one is visible — that
 * keeps each page's markup declarative, and it means a test can address the
 * table and the cards separately.
 *
 * The cards deliberately carry fewer controls than the rows they replace.
 * Bulk selection and inline row actions need pointer precision a thumb does
 * not have, so on a phone a card is a summary you tap to open, and the detail
 * screen is where things happen. Where a list has no detail screen to open,
 * the card keeps its one action.
 */

/** The card list. Hidden from `md` up, where the real table takes over. */
export function MobileCardList({ children, className, ...props }) {
  return (
    <div className={cn('space-y-2 md:hidden', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * One record as a card. Tappable when `onClick` is given — rendered as a
 * button so it is reachable by keyboard and announced as an action, rather
 * than a div with a click handler.
 */
export function MobileCard({ onClick, children, className, ...props }) {
  const shared = cn(
    'w-full rounded-xl border border-[rgba(6,43,26,0.10)] bg-white p-3 text-left shadow-sm',
    onClick && 'transition-colors hover:bg-[#F7F8FA] active:bg-[#F1F3F5]',
    controlFocusClass,
    className,
  );
  if (!onClick) {
    return <div className={shared} {...props}>{children}</div>;
  }
  return (
    <button type="button" onClick={onClick} className={shared} {...props}>
      {children}
    </button>
  );
}

/** The card's top line: what the record is, and its status. */
export function MobileCardHeader({ title, meta, badge }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-[#062B1A]">{title}</p>
        {meta && <p className="truncate text-xs text-[#062B1A]/55">{meta}</p>}
      </div>
      {badge && <span className="shrink-0">{badge}</span>}
    </div>
  );
}

/**
 * A labelled value inside a card.
 *
 * The label is always shown. A table gets to put its column names in a header
 * row once; a card has to say what each value is every time, or it becomes a
 * pile of unattributed strings.
 */
export function MobileCardField({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.06em] text-[#062B1A]/40">
        {label}
      </span>
      <span className="min-w-0 truncate text-right text-xs text-[#062B1A]/80">{children}</span>
    </div>
  );
}

/** The fields block: a hairline above, comfortable spacing between rows. */
export function MobileCardFields({ children, className }) {
  return (
    <div className={cn('mt-2 space-y-1 border-t border-[rgba(6,43,26,0.07)] pt-2', className)}>
      {children}
    </div>
  );
}

/** Wraps the real table so it only appears once there is room for it. */
export function DesktopTableOnly({ children, className }) {
  return <div className={cn('hidden md:block', className)}>{children}</div>;
}

/** "Nothing here" / "still loading", in the card column. */
export function MobileCardNotice({ children }) {
  return (
    <p className="rounded-xl border border-dashed border-[rgba(6,43,26,0.15)] bg-white px-4 py-10 text-center text-sm text-[#062B1A]/45">
      {children}
    </p>
  );
}
